/** Low-level storage contract — matches the Web Storage API subset. */
export type StorageAdapter = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Immutable snapshot of session scores */
export type ScoreMap = Readonly<Record<string, number>>;

export interface Session {
  /** Current scores snapshot (read-only view) */
  readonly scores: ScoreMap;

  /** Returns the score for a level, or null if none recorded */
  getScore(levelId: string): number | null;

  /**
   * Records a score if it's a new high.
   * Returns true if the score was actually persisted (i.e. it beat the previous).
   */
  recordScore(levelId: string, score: number): boolean;

  /** Wipes all saved data and returns a fresh session */
  reset(): void;
}

const STORAGE_KEY = 'html-game-ai-harness:save';

interface SaveData {
  scores: Record<string, number>;
}

function loadFromStorage(storage: StorageAdapter): Record<string, unknown> {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function flushToStorage(data: SaveData, storage: StorageAdapter): void {
  storage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function normalizeSave(data: Record<string, unknown>): SaveData {
  const scores =
    data.scores && typeof data.scores === 'object' ? (data.scores as Record<string, number>) : {};
  return { scores };
}

/** Creates a Session backed by the given storage adapter (defaults to localStorage). */
export function createSession(storage: StorageAdapter = localStorage): Session {
  let data = normalizeSave(loadFromStorage(storage));

  return {
    get scores(): ScoreMap {
      return data.scores;
    },

    getScore(levelId: string): number | null {
      return data.scores[levelId] ?? null;
    },

    recordScore(levelId: string, score: number): boolean {
      const existing = data.scores[levelId];
      if (existing !== undefined && score <= existing) {
        return false;
      }
      data.scores[levelId] = score;
      flushToStorage(data, storage);
      return true;
    },

    reset(): void {
      data = { scores: {} };
      storage.removeItem(STORAGE_KEY);
    },
  };
}

/** In-memory storage adapter for testing — no DOM, no mocking needed. */
export function createMemoryStorage(): StorageAdapter {
  const store = new Map<string, string>();
  return {
    getItem: (k) => store.get(k) ?? null,
    setItem: (k, v) => {
      store.set(k, v);
    },
    removeItem: (k) => {
      store.delete(k);
    },
  };
}
